import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import MarketingShell from "./MarketingShell";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import TestSequenceButton from "./TestSequenceButton";
import { ChartBar, X } from "@phosphor-icons/react";
import { formatLocal, asUtc, userTz } from "@/lib/timezone";

/* The analytics dashboard shows a unified table covering marketing
 * workflows (`mkt_workflows`). The drawer at the right shows the
 * workflow's per-event breakdown.
 *
 * Why one component file: the table + drawer share state (selected row)
 * and the drawer's data shape is small enough that splitting to its own
 * file would be premature. We only refactor when this hits ~300 lines or
 * needs reuse elsewhere. */

const KPI_TILES = [
  { key: "enrolled", label: "Enrolled" },
  { key: "sent", label: "Sent" },
  { key: "delivered", label: "Delivered" },
  { key: "opened", label: "Opened", rateKey: "open_rate" },
  { key: "bounced", label: "Bounced", rateKey: "bounce_rate" },
  { key: "replied", label: "Replied", rateKey: "reply_rate" },
];

export default function AnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [headline, setHeadline] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null); // {id, type, name}
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketing/analytics/summary");
      setHeadline(r.data.headline);
      setItems(r.data.items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // When the user clicks a row, fetch the drawer payload. Decoupled
  // from rendering so a second click on the same row doesn't re-fetch.
  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    api.get(`/marketing/analytics/${selected.type}/${selected.id}`)
      .then((r) => { if (!cancelled) setDetail(r.data); })
      .catch(() => { if (!cancelled) setDetail(null); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selected]);

  return (
    <MarketingShell>
      <div data-testid="analytics-page" className="space-y-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">Performance</p>
          <h2 className="text-xl font-black tracking-tight text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Analytics
          </h2>
          <p className="text-[12px] text-[#525252] mt-1">
            Funnel and engagement totals for every workflow, since each launched.
          </p>
        </div>

        {/* Recent Sends — answers "did anything go out today?" without
            having to drill into every workflow's history tab. */}
        <RecentSendsPanel />

        {/* Headline KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {KPI_TILES.map((tile) => (
            <KPITile
              key={tile.key}
              label={tile.label}
              value={loading ? null : (headline?.[tile.key] ?? 0)}
              rate={tile.rateKey && headline ? headline[tile.rateKey] : null}
              testId={`kpi-${tile.key}`}
            />
          ))}
        </div>

        {/* Items table */}
        <div className="bg-white border border-[#E5E5E5]">
          <div className="grid grid-cols-[minmax(0,2fr)_5rem_5rem_5rem_5rem_5rem_5rem_5rem_5rem_5rem] gap-2 items-center px-4 py-2 bg-[#FAFAFA] border-b border-[#E5E5E5] text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">
            <span>Name</span>
            <span className="text-right">Type</span>
            <span className="text-right">Enrolled</span>
            <span className="text-right">Sent</span>
            <span className="text-right">Delivered</span>
            <span className="text-right">Opened</span>
            <span className="text-right">Clicked</span>
            <span className="text-right">Bounced</span>
            <span className="text-right">Unsub</span>
            <span className="text-right">Replied</span>
          </div>
          {loading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-[12px] text-[#A3A3A3]" data-testid="analytics-empty">
              No campaigns or workflows yet.
            </div>
          ) : items.map((it) => (
            <button
              key={`${it.type}-${it.id}`}
              data-testid={`analytics-row-${it.id}`}
              onClick={() => setSelected({ id: it.id, type: it.type, name: it.name })}
              className="w-full text-left grid grid-cols-[minmax(0,2fr)_5rem_5rem_5rem_5rem_5rem_5rem_5rem_5rem_5rem] gap-2 items-center px-4 py-2.5 border-b border-[#F5F5F5] hover:bg-[#FAFAFA] transition-colors"
            >
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-[#0A0A0A] truncate">{it.name}</div>
                {it.description && (
                  <div className="text-[11px] text-[#A3A3A3] truncate">{it.description}</div>
                )}
              </div>
              <span className="text-right text-[11px] uppercase font-bold tracking-wide text-[#525252]">{it.type}</span>
              <span className="text-right text-[13px] tabular-nums">{it.enrolled}</span>
              <span className="text-right text-[13px] tabular-nums">{it.sent}</span>
              <span className="text-right text-[13px] tabular-nums">{it.delivered}</span>
              <span className="text-right text-[13px] tabular-nums">
                {it.opened}
                {it.delivered > 0 && (
                  <span className="block text-[10px] text-[#A3A3A3]">{it.open_rate}%</span>
                )}
              </span>
              <span className="text-right text-[13px] tabular-nums">{it.clicked}</span>
              <span className={`text-right text-[13px] tabular-nums ${it.bounced > 0 ? "text-[#B91C1C]" : ""}`}>
                {it.bounced}
              </span>
              <span className="text-right text-[13px] tabular-nums">{it.unsubscribed}</span>
              <span className={`text-right text-[13px] tabular-nums ${it.replied > 0 ? "text-[#0055FF] font-semibold" : ""}`}>
                {it.type === "workflow" ? <span className="text-[#A3A3A3]" title="Workflow replies are tracked at the lead level, not yet aggregated per workflow.">—</span> : it.replied}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Drawer */}
      <AnalyticsDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        selected={selected}
        detail={detail}
        loading={detailLoading}
      />

      {/* Top clicked links */}
      <TopClickedLinksCard />
    </MarketingShell>
  );
}


/** Recent Sends — at-a-glance "what went out today / yesterday / last 7
 *  days" tile. Sits above the headline KPI grid because the user's #1
 *  question on the analytics page is "did anything go out today?", and
 *  the existing KPI tiles are all-time aggregates that don't answer
 *  that question. Poll-once-on-mount; hot dispatcher state can drift
 *  by up to a minute and that's fine for an operator dashboard.
 *  Self-hides when the system is brand-new (zero sends in 7 days)
 *  with a teaching empty-state so a freshly-launched tenant isn't
 *  confused by a wall of zeros. */
function RecentSendsPanel() {
  const [data, setData] = useState(null);
  const [includeTest, setIncludeTest] = useState(false);
  const [drillBucket, setDrillBucket] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const url = `/marketing/recent-sends${includeTest ? "?include_test=true" : ""}`;
    api.get(url)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [includeTest]);

  if (!data) {
    return (
      <div className="bg-white border border-[#E5E5E5] p-4">
        <Skeleton className="h-4 w-32 mb-2" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  const fmtTime = (iso) => {
    if (!iso) return "—";
    // Use the viewer's profile timezone (Mike → Pacific). `formatLocal`
    // also calls `asUtc()` to add the missing "Z" suffix on naive ISO
    // strings emitted by SQL Server DATETIME2 columns — without that
    // step the browser would interpret the string as local time and
    // shift the clock face by the local UTC offset.
    return formatLocal(iso, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      year: undefined, withZoneName: true,
    });
  };

  const breakdownRows = [
    ...(data.workflows || []).map(w => ({ ...w, kind: "Workflow" })),
    ...(data.campaigns || []).map(c => ({ ...c, kind: "Campaign" })),
    ...(data.crm_drip ? [{ ...data.crm_drip, kind: "CRM Drip", name: "Cold-leads + manual reminders", id: "crm" }] : []),
  ];

  return (
    <div className="bg-white border border-[#E5E5E5]" data-testid="recent-sends-panel">
      <div className="px-4 py-2.5 border-b border-[#E5E5E5] flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#525252]">Recent sends</p>
          <p className="text-[10px] text-[#A3A3A3]">
            "Did anything go out today?" Per-source breakdown for today + yesterday.
            <span className="ml-1.5" data-testid="recent-sends-tz">Times in your tz ({userTz()}).</span>
          </p>
        </div>
        <label className="flex items-center gap-1.5 text-[10px] text-[#525252] cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeTest}
            onChange={e => setIncludeTest(e.target.checked)}
            className="cursor-pointer"
            data-testid="recent-sends-include-test-toggle"
          />
          include test sends
        </label>
      </div>

      {/* Three big bucket tiles */}
      <div className="grid grid-cols-3 divide-x divide-[#E5E5E5]">
        <BucketTile label="Today" value={data.today} accent="#0055FF" testId="recent-sends-today" onClick={() => setDrillBucket("today")} />
        <BucketTile label="Yesterday" value={data.yesterday} accent="#525252" testId="recent-sends-yesterday" onClick={() => setDrillBucket("yesterday")} />
        <BucketTile label="Last 7 days" value={data.last_7d} accent="#16A34A" testId="recent-sends-7d" onClick={() => setDrillBucket("last_7d")} />
      </div>

      {/* Breakdown table */}
      {(data.today + data.yesterday) === 0 ? (
        <div className="p-4 text-[11px] text-[#A3A3A3] border-t border-[#E5E5E5]" data-testid="recent-sends-empty">
          No sends in the last 24 hours.{" "}
          {data.last_7d > 0
            ? <>Earliest in window: <strong className="text-[#525252]">{data.last_7d} sends</strong> in the last 7 days — open a workflow's analytics tab to see history.</>
            : <>If you expected sends, check the cap status on <a href="/marketing/workflows" className="underline">/marketing/workflows</a> (a red banner means outbound is paused).</>}
        </div>
      ) : (
        <div className="border-t border-[#E5E5E5]">
          <table className="w-full text-[11px]">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#525252]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Source</th>
                <th className="text-left px-3 py-2 font-semibold">Type</th>
                <th className="text-right px-3 py-2 font-semibold">Today</th>
                <th className="text-right px-3 py-2 font-semibold">Yesterday</th>
                <th className="text-left px-3 py-2 font-semibold">Last send</th>
              </tr>
            </thead>
            <tbody>
              {breakdownRows.map(r => (
                <tr key={`${r.kind}-${r.id}`} className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA]"
                    data-testid={`recent-sends-row-${r.kind.toLowerCase().replace(/\s/g, "-")}-${r.id}`}>
                  <td className="px-3 py-2 text-[#0A0A0A]">
                    {r.kind !== "CRM Drip" ? (
                      <a href={`/marketing/${r.kind === "Workflow" ? "workflows" : "campaigns"}/${r.id}?tab=email-history`}
                         className="hover:underline">
                        {r.name}
                      </a>
                    ) : r.name}
                  </td>
                  <td className="px-3 py-2 text-[#A3A3A3]">{r.kind}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold text-[#0055FF]">{r.today}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#525252]">{r.yesterday}</td>
                  <td className="px-3 py-2 text-[#525252]">{fmtTime(r.last_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drillBucket && (
        <RecentSendsDrilldown
          bucket={drillBucket}
          includeTest={includeTest}
          onClose={() => setDrillBucket(null)}
        />
      )}
    </div>
  );
}

function BucketTile({ label, value, accent, testId, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-4 py-3 text-left hover:bg-[#FAFAFA] transition-colors group"
      data-testid={testId}
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] flex items-center gap-1" style={{ color: accent }}>
        {label}
        <span className="text-[8px] opacity-0 group-hover:opacity-100 transition-opacity">VIEW →</span>
      </p>
      <p className="text-2xl font-black tabular-nums text-[#0A0A0A] mt-0.5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
        {value}
      </p>
      <p className="text-[10px] text-[#A3A3A3] mt-0.5">{value === 1 ? "send" : "sends"} · click for details</p>
    </button>
  );
}

/** Drill-down modal: lists actual sends for a bucket. */
function RecentSendsDrilldown({ bucket, includeTest, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({
      bucket,
      ...(includeTest ? { include_test: "true" } : {}),
      limit: "200",
    });
    api.get(`/marketing/recent-sends/items?${params}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ items: [], total: 0 }); });
    return () => { cancelled = true; };
  }, [bucket, includeTest]);

  const bucketLabel = { today: "Today", yesterday: "Yesterday", last_7d: "Last 7 days" }[bucket];
  const fmtTs = (iso) => {
    if (!iso) return "—";
    return formatLocal(iso, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      year: undefined, withZoneName: true,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}
         data-testid="recent-sends-drilldown">
      <div className="bg-white border border-[#0A0A0A] w-full max-w-5xl max-h-[85vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-baseline justify-between gap-2">
          <div>
            <h3 className="text-sm font-bold text-[#0A0A0A]">Recent sends — {bucketLabel}</h3>
            <p className="text-[10px] text-[#A3A3A3]">
              {data ? `${data.total} send${data.total === 1 ? "" : "s"}` : "Loading…"}
              {includeTest ? " (incl. test sends)" : ""}
              <span className="ml-2 text-[#A3A3A3]" data-testid="recent-sends-drilldown-tz">· times in your tz ({userTz()})</span>
            </p>
          </div>
          <button onClick={onClose} className="text-xs text-[#525252] hover:text-[#0A0A0A] px-2 py-1"
                  data-testid="recent-sends-drilldown-close">Close ✕</button>
        </div>
        <div className="overflow-auto flex-1">
          {!data ? (
            <div className="p-8 text-center"><Skeleton className="h-32 w-full" /></div>
          ) : data.items.length === 0 ? (
            <p className="p-8 text-center text-[11px] text-[#A3A3A3]">No sends in this window.</p>
          ) : (
            <table className="w-full text-[11px]">
              <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#525252] sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">When</th>
                  <th className="text-left px-3 py-2 font-semibold">Recipient</th>
                  <th className="text-left px-3 py-2 font-semibold">Source</th>
                  <th className="text-left px-3 py-2 font-semibold">Subject</th>
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(it => (
                  <tr key={`${it.kind}-${it.id}`} className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA]"
                      data-testid={`drilldown-row-${it.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-[#525252] font-mono">{fmtTs(it.at)}</td>
                    <td className="px-3 py-2 text-[#0A0A0A]">
                      {it.contact_email ? (
                        <>
                          <div className="font-medium">{it.contact_email}</div>
                          {it.contact_name && <div className="text-[10px] text-[#A3A3A3]">{it.contact_name}</div>}
                        </>
                      ) : (
                        <span className="text-[#A3A3A3] italic">{it.contact_name || "(CRM lead)"}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[#525252]">
                      <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                        it.kind === "workflow" ? "bg-[#F0F7FF] text-[#0055FF]" :
                        it.kind === "campaign" ? "bg-[#F0FDF4] text-[#16A34A]" :
                        "bg-[#FEF3C7] text-[#92400E]"
                      }`}>{it.kind === "crm_drip" ? "CRM" : it.kind}</span>
                      <span className="ml-1.5 text-[#525252]">{it.source_name}</span>
                      {it.step_index !== null && it.step_index !== undefined && (
                        <span className="ml-1 text-[10px] text-[#A3A3A3]">· step {it.step_index}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[#0A0A0A] max-w-md truncate" title={it.subject || ""}>{it.subject || <span className="text-[#A3A3A3] italic">—</span>}</td>
                    <td className="px-3 py-2"><span className="text-[10px] text-[#525252] uppercase">{it.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


/** Shows the URLs getting the most engagement across every campaign +
 *  workflow in the selected time window. Polls a lightweight endpoint
 *  (`/marketing/analytics/top-links`) and lists each URL with a click
 *  count, unique clickers, last-click timestamp, and a header/body
 *  tag so marketers can spot at-a-glance whether the new nav-header
 *  icons are pulling real engagement. Empty-state teaches marketers
 *  what to expect so a freshly-launched system isn't confusing. */
function TopClickedLinksCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/marketing/analytics/top-links?days=${windowDays}&limit=15`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [windowDays]);

  const items = data?.items || [];

  return (
    <div className="bg-white border border-[#E5E5E5] mt-6" data-testid="top-clicked-links-card">
      <div className="flex items-end justify-between gap-4 p-4 border-b border-[#E5E5E5]">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">Engagement</p>
          <h3 className="text-lg font-black tracking-tight text-[#0A0A0A] mt-0.5"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Top Clicked Links
          </h3>
          <p className="text-[11px] text-[#525252] mt-1">
            Ranks every URL clicked in sent campaigns — useful for comparing header icons vs body CTAs.
          </p>
        </div>
        <div className="flex items-center gap-1.5" data-testid="top-links-window-toggle">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setWindowDays(d)}
              className={`px-2.5 h-7 text-[11px] font-bold tabular-nums tracking-wider uppercase border ${
                windowDays === d
                  ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                  : "bg-white text-[#525252] border-[#E5E5E5] hover:border-[#0A0A0A]"
              }`}
              data-testid={`top-links-window-${d}d`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Headline row */}
      <div className="grid grid-cols-3 divide-x divide-[#E5E5E5] border-b border-[#E5E5E5]">
        <MiniStat label="Total clicks"     value={loading ? null : data?.total_clicks}   />
        <MiniStat label="Unique URLs"      value={loading ? null : data?.unique_urls}    />
        <MiniStat label="Unique clickers"  value={loading ? null : data?.unique_clickers}/>
      </div>

      {/* Table */}
      {loading ? (
        <div className="p-6 space-y-2">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center">
          <p className="text-[12px] text-[#A3A3A3]">
            No clicks in the last {windowDays} days yet. Links from sent campaigns will appear here once recipients start engaging.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-[24px_1fr_60px_80px_80px_120px] gap-3 items-center text-[11px] font-bold uppercase tracking-wider text-[#A3A3A3] px-4 py-2 border-b border-[#F5F5F5]">
          <span>#</span>
          <span>Link</span>
          <span className="text-right">Type</span>
          <span className="text-right">Clicks</span>
          <span className="text-right">Unique</span>
          <span className="text-right">Last click</span>
        </div>
      )}
      {!loading && items.map((it, idx) => (
        <a
          key={`${it.url}-${idx}`}
          href={it.url}
          target="_blank"
          rel="noopener noreferrer"
          className="grid grid-cols-[24px_1fr_60px_80px_80px_120px] gap-3 items-center px-4 py-2.5 border-b border-[#F5F5F5] last:border-b-0 hover:bg-[#FAFAFA] transition-colors"
          data-testid={`top-link-row-${idx}`}
          title={it.url}
        >
          <span className="text-[12px] font-black text-[#A3A3A3] tabular-nums">{idx + 1}</span>
          <span className="text-[12px] text-[#0055FF] truncate hover:underline">
            {(() => { try { return new URL(it.url).pathname + new URL(it.url).search; } catch { return it.url; } })()}
          </span>
          <span className="text-right">
            <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
              it.source === "header"
                ? "bg-[#EEF2FA] text-[#273b86] border border-[#C5D0E4]"
                : "bg-[#F5F5F5] text-[#525252] border border-[#E5E5E5]"
            }`}>
              {it.source}
            </span>
          </span>
          <span className="text-right text-[13px] font-bold tabular-nums text-[#0A0A0A]">
            {it.clicks.toLocaleString()}
          </span>
          <span className="text-right text-[13px] tabular-nums text-[#525252]">
            {it.unique_clickers.toLocaleString()}
          </span>
          <span className="text-right text-[11px] tabular-nums text-[#A3A3A3]">
            {formatShortRelative(it.last_click_at)}
          </span>
        </a>
      ))}
    </div>
  );
}


function MiniStat({ label, value }) {
  return (
    <div className="p-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#A3A3A3]">{label}</div>
      <div className="mt-0.5 text-xl font-black tabular-nums text-[#0A0A0A]"
           style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
        {value === null || value === undefined ? <Skeleton className="h-6 w-14" /> : value.toLocaleString()}
      </div>
    </div>
  );
}

function formatShortRelative(iso) {
  if (!iso) return "—";
  // Naive timestamps from SQL Server need to be normalised before
  // calling getTime — otherwise the relative offset is wrong by the
  // viewer's UTC delta. `formatLocal` re-uses the same `asUtc` helper
  // for the trailing "Apr 17, 2026" branch.
  const then = new Date(asUtc(iso));
  if (Number.isNaN(then.getTime())) return "—";
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1)      return "just now";
  if (mins < 60)     return `${mins}m ago`;
  if (mins < 1440)   return `${Math.floor(mins / 60)}h ago`;
  if (mins < 10080)  return `${Math.floor(mins / 1440)}d ago`;
  return formatLocal(iso, { year: "numeric", month: "short", day: "2-digit", withZoneName: false });
}


function KPITile({ label, value, rate, testId }) {
  return (
    <div className="bg-white border border-[#E5E5E5] p-4" data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#A3A3A3]">{label}</div>
      <div className="mt-1 text-2xl font-black tabular-nums text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
        {value === null ? <Skeleton className="h-7 w-16" /> : value.toLocaleString()}
      </div>
      {rate !== null && rate !== undefined && (
        <div className="text-[11px] text-[#525252] mt-0.5">{rate}%</div>
      )}
    </div>
  );
}


export function AnalyticsDrawer({ open, onClose, selected, detail, loading }) {
  if (!selected) return null;
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="analytics-drawer">
        <SheetHeader className="px-6 pt-6 pb-3 border-b border-[#E5E5E5]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">
                {selected.type} · funnel
              </p>
              <SheetTitle className="text-xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {selected.name}
              </SheetTitle>
              {detail?.summary?.description && (
                <p className="text-[12px] text-[#525252] mt-1">{detail.summary.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <TestSequenceButton
                kind={selected.type}
                id={selected.id}
                sequenceName={selected.name}
              />
              <button
                onClick={onClose}
                className="text-[#A3A3A3] hover:text-[#0A0A0A] transition-colors p-1"
                data-testid="analytics-drawer-close"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </SheetHeader>

        {loading ? (
          <div className="p-6 space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : !detail ? (
          <div className="p-6 text-[12px] text-[#A3A3A3]">Failed to load detail.</div>
        ) : (
          <WorkflowDetail detail={detail} />
        )}
      </SheetContent>
    </Sheet>
  );
}


function WorkflowDetail({ detail }) {
  // Fetch the per-workflow OR per-campaign email-history (sent + scheduled)
  // on mount. Lazy-loaded inside the drawer so opening it doesn't block on
  // this round-trip — the summary still renders instantly.
  const itemId = detail?.summary?.id;
  const itemKind = detail?.summary?.type || "workflow";
  const [history, setHistory] = useState({ sent: [], scheduled: [], counts: { sent: 0, scheduled: 0 } });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;
    setLoading(true);
    const path = itemKind === "campaign"
      ? `/marketing/campaigns/${itemId}/email-history?status=all&limit=200`
      : `/marketing/workflows/${itemId}/email-history?status=all&limit=200`;
    api.get(path)
      .then(r => { if (!cancelled) setHistory(r.data || { sent: [], scheduled: [], counts: { sent: 0, scheduled: 0 } }); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [itemId, itemKind]);

  return (
    <div className="px-6 py-5 space-y-6">
      <SummaryStrip s={detail.summary} />

      <EmailHistoryTabs
        sent={history.sent}
        scheduled={history.scheduled}
        loading={loading}
        kind={itemKind}
      />

      <section>
        <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3] mb-2">
          Event breakdown
        </h3>
        <div className="bg-white border border-[#E5E5E5]">
          <div className="grid grid-cols-3 gap-2 px-3 py-2 bg-[#FAFAFA] border-b border-[#E5E5E5] text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">
            <span>Event</span>
            <span className="text-right">Unique sends</span>
            <span className="text-right">Event count</span>
          </div>
          {(detail.event_breakdown || []).length === 0 ? (
            <div className="px-3 py-4 text-[12px] text-[#A3A3A3]">No events yet.</div>
          ) : (detail.event_breakdown || []).map((b) => (
            <div key={b.event_type} className="grid grid-cols-3 gap-2 px-3 py-2 border-b border-[#F5F5F5] text-[12px]">
              <span className="font-semibold text-[#0A0A0A] capitalize">{b.event_type}</span>
              <span className="text-right tabular-nums">{b.unique_sends}</span>
              <span className="text-right tabular-nums text-[#525252]">{b.event_count}</span>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3] mb-2">
          Recent events
        </h3>
        <EventFeed events={detail.recent_events || []} compact />
      </section>
    </div>
  );
}


function EmailHistoryTabs({ sent, scheduled, loading, kind }) {
  // Two-tab view: "Sent" (newest first, what's gone out) and
  // "Scheduled" (soonest first, what's still queued). Tabs are local
  // state — the parent passes both arrays so we never refetch when
  // toggling. `kind` is 'workflow' | 'campaign' so we can hide the
  // Step column for campaigns (which don't have step indices).
  const [tab, setTab] = useState("scheduled");
  const rows = tab === "sent" ? sent : scheduled;
  const fmt = (iso) => {
    if (!iso) return "—";
    return formatLocal(iso, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", year: undefined, withZoneName: true });
  };
  return (
    <section data-testid="email-history-card">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">Email history</h3>
        <div className="ml-auto inline-flex bg-white border border-[#E5E5E5] p-0.5">
          {[
            { id: "scheduled", label: "Due", n: scheduled?.length || 0 },
            { id: "sent",      label: "Sent", n: sent?.length || 0 },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-2.5 h-6 text-[10px] font-bold uppercase tracking-wider ${tab === t.id ? "bg-[#0A0A0A] text-white" : "text-[#525252] hover:bg-[#FAFAFA]"}`}
              data-testid={`history-tab-${t.id}`}
            >
              {t.label} <span className="text-[9px] opacity-70 ml-1">{t.n}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white border border-[#E5E5E5]">
        {loading ? (
          <div className="px-3 py-4 text-[12px] text-[#A3A3A3]">Loading…</div>
        ) : (rows || []).length === 0 ? (
          <div className="px-3 py-4 text-[12px] text-[#A3A3A3]" data-testid={`history-empty-${tab}`}>
            {tab === "sent" ? "No emails have gone out yet." : "Nothing queued."}
          </div>
        ) : (
          <table className="w-full text-[12px]" data-testid={`history-table-${tab}`}>
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Recipient</th>
                {kind === "workflow" && <th className="text-left px-3 py-2 font-bold">Step</th>}
                <th className="text-left px-3 py-2 font-bold">Subject</th>
                <th className="text-left px-3 py-2 font-bold whitespace-nowrap">{tab === "sent" ? "Sent" : "Due"}</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.send_id} className="border-t border-[#F5F5F5]">
                  <td className="px-3 py-2">
                    <p className="font-semibold text-[#0A0A0A] truncate max-w-[180px]" title={r.contact_email}>{r.contact_name || r.contact_email}</p>
                    {r.contact_name && r.contact_email && (
                      <p className="text-[10px] text-[#A3A3A3] truncate max-w-[180px]">{r.contact_email}</p>
                    )}
                  </td>
                  {kind === "workflow" && <td className="px-3 py-2 text-[#525252] tabular-nums">{r.step_index ?? "—"}</td>}
                  <td className="px-3 py-2 truncate max-w-[260px]" title={r.subject || ""}>{r.subject || <span className="text-[#A3A3A3]">—</span>}</td>
                  <td className="px-3 py-2 text-[#525252] whitespace-nowrap">{fmt(tab === "sent" ? r.sent_at : r.scheduled_for)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                      r.status === "bounced" ? "bg-[#FEF2F2] text-[#B91C1C]" :
                      r.status === "queued" || r.status === "scheduled" ? "bg-[#FFFBEB] text-[#D97706]" :
                      "bg-[#F0FDF4] text-[#16A34A]"
                    }`}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}


function SummaryStrip({ s }) {
  if (!s) return null;
  const cards = [
    { l: "Enrolled", v: s.enrolled },
    { l: "Sent", v: s.sent },
    { l: "Delivered", v: s.delivered, sub: s.sent ? `${Math.round(100 * s.delivered / s.sent)}%` : null },
    { l: "Opened", v: s.opened, sub: `${s.open_rate}%` },
    { l: "Clicked", v: s.clicked, sub: `${s.click_rate}%` },
    { l: "Bounced", v: s.bounced, sub: s.bounce_rate ? `${s.bounce_rate}%` : null, danger: s.bounced > 0 },
    { l: "Unsub", v: s.unsubscribed },
    { l: "Replied", v: s.replied, sub: s.reply_rate ? `${s.reply_rate}%` : null, success: s.replied > 0 },
  ];
  return (
    <div className="grid grid-cols-4 gap-2">
      {cards.map((c) => (
        <div key={c.l} className="bg-white border border-[#E5E5E5] p-3">
          <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#A3A3A3]">{c.l}</div>
          <div className={`mt-1 text-lg font-black tabular-nums ${c.danger ? "text-[#B91C1C]" : c.success ? "text-[#0055FF]" : "text-[#0A0A0A]"}`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {c.v?.toLocaleString?.() ?? 0}
          </div>
          {c.sub && <div className="text-[10px] text-[#525252]">{c.sub}</div>}
        </div>
      ))}
    </div>
  );
}


function EventFeed({ events, compact }) {
  if (!events.length) {
    return <div className="text-[12px] text-[#A3A3A3]">No events recorded yet.</div>;
  }
  // Map event_type → CSS class for the small color chip on the left.
  const chipColor = {
    replied: "bg-[#0055FF]",
    delivered: "bg-[#16A34A]",
    opened: "bg-[#0EA5E9]",
    clicked: "bg-[#7C3AED]",
    bounced: "bg-[#B91C1C]",
    unsubscribed: "bg-[#A3A3A3]",
    complained: "bg-[#DC2626]",
    automated: "bg-[#525252]",
    email_sent: "bg-[#0A0A0A]",
  };
  return (
    <div className="bg-white border border-[#E5E5E5] divide-y divide-[#F5F5F5]" data-testid="event-feed">
      {events.map((e, i) => (
        <div key={i} className="flex items-start gap-2 px-3 py-2 text-[12px]">
          <span className={`w-2 h-2 mt-1.5 ${chipColor[e.event_type] || "bg-[#A3A3A3]"}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="font-semibold capitalize text-[#0A0A0A]">{e.event_type?.replace(/_/g, " ")}</span>
              <span className="text-[10px] text-[#A3A3A3]">{formatTime(e.at)}</span>
            </div>
            {!compact && (e.description || e.url) && (
              <div className="text-[11px] text-[#525252] truncate">{e.description || e.url}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}


function formatTime(iso) {
  if (!iso) return "";
  return formatLocal(iso, { dateStyle: "short", timeStyle: "short", withZoneName: true });
}
